<script setup lang="ts">
import { PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * Um painel que flutua ao lado do que o abriu, com conteúdo livre.
 *
 * ── POR QUE PORTAL, E NÃO UM z-index MAIOR ───────────────────────────────
 *
 * Porque `z-index` não vence `overflow`. Há 98 ancestrais com `overflow-*` no
 * app — uma coluna do quadro, a lista da inbox, o corpo de um card — e um painel
 * `absolute` dentro de qualquer um deles é CORTADO, por mais alto que esteja
 * empilhado. Sair do fluxo é a única coisa que resolve; e, já fora, a colisão
 * (`avoid-collisions` com 8px de folga) é o que apaga as gambiarras de
 * `max-w-[calc(100vw-2rem)]` que os painéis escreviam à mão para não sangrar
 * pela direita numa janela estreita.
 *
 * A moldura é daqui; largura e altura são de quem chama, por `panelClass` — um
 * seletor de emoji e um menu de conta não têm a mesma medida e não deveriam
 * combinar uma.
 */
const props = withDefaults(
	defineProps<{
		open: boolean;
		/** O painel sai do fluxo: sem nome, um leitor de tela anuncia "grupo". */
		label: string;
		side?: "top" | "bottom" | "left" | "right";
		align?: "start" | "center" | "end";
		panelClass?: string;
		/**
		 * O gatilho apenas ANCORA: quem decide abrir é quem chama, e o foco fica
		 * onde está. É a forma de um campo com sugestões — a lista aparece
		 * enquanto se digita, e puxar o foco para dentro dela pararia a digitação.
		 */
		anchor?: boolean;
	}>(),
	{ side: "bottom", align: "start", panelClass: "", anchor: false },
);

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const anchorEl = ref<HTMLElement | null>(null);

const classes = computed(() =>
	twMerge(
		"z-50 rounded-xl border border-border bg-surface-raised shadow-md focus:outline-none",
		props.panelClass,
	),
);

/**
 * Em modo âncora o gatilho não é gatilho da reka, então um clique nele contaria
 * como "fora" e fecharia o painel que aquele mesmo clique acabou de pedir.
 */
function insideAnchor(event: { detail: { originalEvent: Event }; preventDefault: () => void }) {
	const target = event.detail.originalEvent.target as Node | null;
	if (props.anchor && target && anchorEl.value?.contains(target)) event.preventDefault();
}

/**
 * Em modo âncora, apertar QUALQUER ponto do painel não tira o foco do campo.
 *
 * O conteúdo da reka é focável (`tabindex="-1"`): um clique na borda, na barra de
 * rolagem ou no respiro entre as opções levava o foco para o painel, o campo
 * perdia o foco no meio da escolha, e o formulário lia esse `blur` como "saiu
 * sem preencher" — "Aluno(a) é obrigatório" antes de a pessoa escolher alguém
 * (teste de 27/09). O `preventDefault` do `mousedown` é o que segura o foco onde
 * ele está; o clique continua a acontecer, e quem está dentro decide o que fazer
 * com ele. Por isso um painel âncora não leva campos próprios: nada ali dentro
 * recebe foco pelo ponteiro, por desenho.
 */
function keepFocusOnAnchor(event: MouseEvent): void {
	if (props.anchor) event.preventDefault();
}
</script>

<template>
	<PopoverRoot :open="open" @update:open="emit('update:open', $event)">
		<PopoverAnchor v-if="anchor" as-child>
			<div ref="anchorEl"><slot name="trigger" /></div>
		</PopoverAnchor>
		<PopoverTrigger v-else as-child>
			<slot name="trigger" />
		</PopoverTrigger>

		<PopoverPortal>
			<PopoverContent
				:class="classes"
				:side="side"
				:align="align"
				:side-offset="6"
				:collision-padding="8"
				:trap-focus="false"
				:aria-label="label"
				avoid-collisions
				@open-auto-focus="anchor && $event.preventDefault()"
				@focus-outside="insideAnchor"
				@pointer-down-outside="insideAnchor"
				@mousedown="keepFocusOnAnchor"
			>
				<slot />
			</PopoverContent>
		</PopoverPortal>
	</PopoverRoot>
</template>
